import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ShieldAlert, Lock, Eye, CheckCircle, AlertTriangle, Shield, Database, FileKey, Clock } from 'lucide-react';

interface RlsLogEntry {
  id: string;
  timestamp: string;
  user: string;
  role: string;
  table: string;
  operation: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE';
  clientId: string;
  result: 'allowed' | 'blocked';
  detail: string;
}

const mockRlsLogs: RlsLogEntry[] = [
  { id: '1', timestamp: '2026-02-10T09:32:14Z', user: 'dr.larkin@hospital.org', role: 'surgeon', table: 'authorization_requests', operation: 'SELECT', clientId: 'Cincinnati Ortho Group', result: 'allowed', detail: 'Fetched own cases (12 rows)' },
  { id: '2', timestamp: '2026-02-10T09:31:55Z', user: 'ops.admin@linkpri.com', role: 'link_ops', table: 'clarification_prompts', operation: 'INSERT', clientId: 'Cincinnati Ortho Group', result: 'allowed', detail: 'Created clarification for case #COG-2026-0412' },
  { id: '3', timestamp: '2026-02-10T09:30:08Z', user: 'external.test@other.org', role: 'surgeon', table: 'authorization_requests', operation: 'SELECT', clientId: 'Other Health System', result: 'blocked', detail: 'Cross-tenant query rejected — client_id mismatch' },
  { id: '4', timestamp: '2026-02-10T09:28:41Z', user: 'dr.chen@hospital.org', role: 'surgeon', table: 'export_packages', operation: 'SELECT', clientId: 'Cincinnati Ortho Group', result: 'blocked', detail: 'Surgeon role prohibited from generating evidence bundle exports' },
  { id: '5', timestamp: '2026-02-10T09:25:19Z', user: 'ops.admin@linkpri.com', role: 'link_ops', table: 'post_op_reconciliation_items', operation: 'UPDATE', clientId: 'Cincinnati Ortho Group', result: 'allowed', detail: 'Reconciliation entry updated for CPT 27447' },
  { id: '6', timestamp: '2026-02-10T09:22:03Z', user: 'anon', role: 'anon', table: 'patients', operation: 'SELECT', clientId: '—', result: 'blocked', detail: 'Unauthenticated access denied' },
];

interface ComplianceItem {
  label: string;
  status: 'verified' | 'active' | 'warning';
  detail: string;
  icon: React.ElementType;
}

const complianceChecks: ComplianceItem[] = [
  { label: 'Encryption at Rest', status: 'verified', detail: 'AES-256 — all tables, backups, and storage buckets encrypted', icon: Lock },
  { label: 'Encryption in Transit', status: 'verified', detail: 'TLS 1.3 enforced — HSTS enabled, certificate valid until 2027-08-15', icon: Shield },
  { label: 'HIPAA BAA Status', status: 'active', detail: 'Business Associate Agreement executed — effective 2025-01-01, auto-renewing', icon: FileKey },
  { label: 'PHI Access Logging', status: 'verified', detail: 'All PHI reads/writes logged with user, role, timestamp, IP — immutable append-only', icon: Eye },
  { label: 'Signed URL Expiry', status: 'verified', detail: 'Document access via time-limited signed URLs (5–15 min) — no persistent links', icon: Clock },
  { label: 'Cross-Tenant Isolation', status: 'verified', detail: 'RLS policies enforce client_id scoping on all tenant-aware tables — cross-tenant joins blocked', icon: Database },
];

const statusBadge = {
  verified: { className: 'text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30', label: 'Verified' },
  active: { className: 'text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/30', label: 'Active' },
  warning: { className: 'text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30', label: 'Attention' },
};

export function SecurityAuditPanel() {
  const blockedCount = mockRlsLogs.filter(l => l.result === 'blocked').length;

  return (
    <div className="space-y-4">
      {/* Compliance posture */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-primary" />
            CISO Security Posture
          </CardTitle>
          <CardDescription>Encryption, BAA, and data isolation verification for Hospital IT audit</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {complianceChecks.map((c, i) => {
              const Icon = c.icon;
              const badge = statusBadge[c.status];
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                  <div className="mt-0.5 p-1.5 rounded bg-primary/10">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{c.label}</span>
                      <Badge variant="outline" className={`text-[10px] ${badge.className}`}>{badge.label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* RLS Logs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4 text-primary" />
                Real-time Row-Level Security Logs
              </CardTitle>
              <CardDescription>Live audit trail of every database access decision</CardDescription>
            </div>
            <div className="flex gap-2 text-sm">
              <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 text-[10px]">
                {mockRlsLogs.length - blockedCount} allowed
              </Badge>
              <Badge variant="destructive" className="text-[10px]">
                {blockedCount} blocked
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="hidden lg:table-cell">Table</TableHead>
                <TableHead className="hidden md:table-cell">Operation</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="hidden xl:table-cell">Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockRlsLogs.map(log => (
                <TableRow key={log.id}>
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-xs font-medium truncate block max-w-[180px]">{log.user}</span>
                      <Badge variant="secondary" className="text-[10px] mt-0.5">{log.role}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <code className="text-xs text-muted-foreground">{log.table}</code>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="outline" className="text-[10px] font-mono">{log.operation}</Badge>
                  </TableCell>
                  <TableCell>
                    {log.result === 'allowed' ? (
                      <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 gap-0.5">
                        <CheckCircle className="w-2.5 h-2.5" />Allowed
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-[10px] gap-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" />Blocked
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <span className="text-xs text-muted-foreground">{log.detail}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
